import { analyzeInlinePromptIssues, resolveAllPromptIssues, type PromptIssue } from '@/lib/promptInlineAnalyzer';
import { normalizeSubject } from '@/lib/subjectUtils';
import { analyzePromptPhysics, fixPromptPhysics, type PhysicsIssue } from '@/lib/promptPhysicsEngine';
import { buildCinematicGrammarReport, enhanceCinematicGrammarLocal, type CinematicGrammarReport, type CinematicQualityLevel } from '@/lib/cinematicGrammarEngine';
import { buildVisualSceneGraph, type VisualSceneGraph } from '@/lib/visualSceneGraphEngine';
import { runHollywoodPromptEngine, type HollywoodPromptReport } from '@/lib/hollywoodPromptEngine';
import { buildSupabaseFunctionUrl } from '@/lib/url';

export type PromptHealthBreakdown = {
  conflicts: number;
  duplicates: number;
  weak: number;
  illogical: number;
  cinematic: number;
  completeness: number;
};

export type PromptSegmentHealth = {
  segment: 'SUBJECT' | 'SHOT TYPE' | 'LIGHTING' | 'COLOR TEMPERATURE' | 'CAMERA' | 'ENVIRONMENT' | 'COMPOSITION' | 'STYLE' | 'QUALITY TAGS';
  status: 'valid' | 'conflict' | 'broken';
  detected: string;
  suggestedFix: string;
};

export type AdvancedConflict = {
  conflictType: string;
  detectedElements: string[];
  explanation: string;
  severity: number;
  location?: { start: number; end: number };
  suggestedFix?: string;
  layer?: 'rule' | 'semantic';
};

export type AdvancedPromptReport = {
  segments: Record<string, string>;
  advancedConflicts: AdvancedConflict[];
  promptScore: number;
  scoreBreakdown: {
    clarity: number;
    structure: number;
    consistency: number;
    visualRichness: number;
    cinematicQuality: number;
    technicalAccuracy: number;
    cleanliness: number;
  };
  scoreReason: string;
  segmentHealth: PromptSegmentHealth[];
  versionA: string;
  versionB: string;
  cinematicVersion: string;
  suggestions: string[];
  finalCleanPrompt: string;
};

export type GrokPromptAnalysis = {
  issues: PromptIssue[];
  health: number;
  breakdown: PromptHealthBreakdown;
  advancedReport?: AdvancedPromptReport;
  raw?: unknown;
};



type SceneUnderstanding = {
  subject: string;
  action: string;
  environment: string;
  lighting: string;
  weather: string;
  mood: string;
  camera: string;
  shotScale: string;
  time: string;
  style: string;
  composition: string;
};

export type ConflictScoreLevel = 'Safe Prompt' | 'Minor Warning' | 'Needs Fix' | 'Broken Prompt';

export type ConflictScore = {
  count: number;
  severityTotal: number;
  level: ConflictScoreLevel;
};

export type PromptIntelligenceResult = {
  userPrompt: string;
  understoodScene: SceneUnderstanding;
  finalPrompt: string;
  conflictReport: AdvancedConflict[];
  semanticConflictReport: AdvancedConflict[];
  conflictScore: ConflictScore;
  conflictWarnings: ConflictWarning[];
  autoDirector: PromptSettingsPreset;
  fixedPrompt: string;
  cinematicPrompt: string;
  promptScore: number;
  physicsReport?: PhysicsIssue[];
  cinematicGrammar: CinematicGrammarReport;
  cinematicQualityLevel: CinematicQualityLevel;
  sceneGraph: VisualSceneGraph;
  hollywoodEngine: HollywoodPromptReport;
};

export type PromptSettingsPreset = {
  camera: string;
  lighting: string;
  style: string;
  composition: string;
  mood: string;
  lens: string;
  palette: string;
  renderQuality: string;
};

export type ConflictWarning = {
  warning: string;
  preventedBy: string;
};

type GrokIssue = {
  text_span: string;
  type: 'conflict' | 'duplicate' | 'weak' | 'illogical';
  reason: string;
  suggested_fix: string;
};


type GrokStructuredConflict = {
  text: string;
  reason: string;
};

type GrokStructuredDuplicate = {
  text: string;
  count: number;
};

type GrokStructuredAnalysis = {
  conflicts?: GrokStructuredConflict[];
  duplicates?: GrokStructuredDuplicate[];
  valid_parts?: string[];
  fixed_prompt?: string;
};

type GrokSemanticResponse = {
  conflicts?: Array<{
    text?: string;
    conflictingWith?: string;
    location?: { start?: number; end?: number };
    suggestion?: string;
    severity?: number;
    explanation?: string;
  }>;
  duplicates?: Array<{
    text?: string;
    locations?: Array<{ start?: number; end?: number }>;
  }>;
  valid?: Array<{ text?: string }>;
};


const XAI_URL = 'https://api.x.ai/v1/chat/completions';
const EDGE_FN_URL = buildSupabaseFunctionUrl(import.meta.env.VITE_SUPABASE_URL, 'prompt-ai');
const analysisCache = new Map<string, GrokPromptAnalysis>();
const intelligenceCache = new Map<string, PromptIntelligenceResult>();

// ── Global rate-limit & request queue ──
let _rateLimitedUntil = 0;
let _inFlightCount = 0;
const MAX_IN_FLIGHT = 1; // Only 1 request at a time
const RATE_LIMIT_BACKOFF_MS = 30_000; // 30s backoff on 429
const MIN_REQUEST_GAP_MS = 3_000; // At least 3s between requests
let _lastRequestTime = 0;

const callPromptAI = async (mode: string, prompt: string, canvasSubject?: string | null): Promise<any> => {
  // Don't send placeholder/meta text to AI
  const trimmed = prompt.trim();
  if (!trimmed || trimmed.startsWith('Place elements') || trimmed.length < 10) return null;

  // Rate limit check
  if (Date.now() < _rateLimitedUntil) {
    console.warn('[callPromptAI] Rate limited, skipping request');
    return null;
  }

  // Concurrency check
  if (_inFlightCount >= MAX_IN_FLIGHT) {
    console.warn('[callPromptAI] Too many in-flight requests, skipping');
    return null;
  }

  // Minimum gap between requests
  const timeSinceLast = Date.now() - _lastRequestTime;
  if (timeSinceLast < MIN_REQUEST_GAP_MS) {
    await new Promise(r => setTimeout(r, MIN_REQUEST_GAP_MS - timeSinceLast));
  }

  _inFlightCount++;
  _lastRequestTime = Date.now();

  try {
    const response = await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ prompt: trimmed, mode, canvasSubject: canvasSubject || 'unknown' }),
    });
    if (response.status === 429) {
      console.warn('[callPromptAI] 429 rate limited, backing off');
      _rateLimitedUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
      return null;
    }
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    _inFlightCount--;
  }
};


const buildIssueFromText = (
  prompt: string,
  text: string,
  type: PromptIssue['type'],
  message: string,
  replacement?: string,
  index = 0,
): PromptIssue | null => {
  const needle = (text || '').trim();
  if (!needle) return null;
  const lower = prompt.toLowerCase();
  const start = lower.indexOf(needle.toLowerCase());
  if (start === -1) return null;
  const end = start + needle.length;
  return {
    id: `grok-structured-${type}-${start}-${end}-${index}`,
    type,
    message,
    reason: message,
    severity: type === 'conflict' || type === 'illogical' ? 'high' : type === 'duplicate' ? 'medium' : 'low',
    start,
    end,
    location: { start, end },
    suggestedFix: replacement,
    fix: replacement !== undefined ? { replacement } : undefined,
  };

};

const mapStructuredAnalysisToIssues = (prompt: string, structured: GrokStructuredAnalysis): PromptIssue[] => {
  const issues: PromptIssue[] = [];

  (structured.conflicts || []).forEach((item, index) => {
    const issue = buildIssueFromText(prompt, item.text, 'conflict', item.reason || 'Logical conflict detected by Grok.', '', index);
    if (issue) issues.push(issue);
  });

  (structured.duplicates || []).forEach((item, index) => {
    const message = `Duplicate phrase appears ${Math.max(2, Number(item.count) || 2)} times.`;
    const issue = buildIssueFromText(prompt, item.text, 'duplicate', message, '', index);
    if (issue) issues.push(issue);
  });

  (structured.valid_parts || []).forEach((item, index) => {
    const issue = buildIssueFromText(prompt, item, 'valid', 'Semantically valid scene element.', undefined, index);
    if (issue) issues.push(issue);
  });

  return issues;
};


const parseJsonObject = (content: string): unknown => {
  try {
    return JSON.parse(content);
  } catch {
    const match = content.match(/\{[\s\S]*\}/);
    if (!match) {
      return null;
    }
    try {
      return JSON.parse(match[0]);
    } catch {
      return null;
    }
  }
};

const normalizeSuggestedReplacement = (type: GrokIssue['type'], suggested: string): string => {
  const clean = (suggested || '').trim();
  if (type === 'duplicate') return '';
  if (!clean) return '';

  if (/\b(remove|delete|omit|drop|consolidate|combine|mention once)\b/i.test(clean)) return '';
  if (/\b(احذف|ازل|إزالة|امسح|شيل)\b/i.test(clean)) return '';
  if (clean.length > 80) return '';

  const words = clean.split(/\s+/).length;
  if (/[.!?]/.test(clean) && words > 8) return '';

  return clean;
};

const mapGrokIssuesToPromptIssues = (prompt: string, grokIssues: GrokIssue[]): PromptIssue[] => {
  const lower = prompt.toLowerCase();

  return grokIssues
    .map((issue, index) => {
      const needle = (issue.text_span || '').trim();
      if (!needle) return null;

      const start = lower.indexOf(needle.toLowerCase());
      if (start === -1) return null;

      const end = start + needle.length;
      const replacement = normalizeSuggestedReplacement(issue.type, issue.suggested_fix || '');

      const promptIssue: PromptIssue = {
        id: `grok-${issue.type}-${start}-${end}-${index}`,
        type: issue.type,
        message: issue.reason,
        reason: issue.reason,
        severity: issue.type === 'conflict' || issue.type === 'illogical' ? 'high' : issue.type === 'duplicate' ? 'medium' : 'low',
        start,
        end,
        location: { start, end },
        suggestedFix: issue.suggested_fix || '',
        fix: {
          replacement,
        },
      };

      return promptIssue;
    })
    .filter((item): item is PromptIssue => Boolean(item));
};

const uniqueIssues = (issues: PromptIssue[]): PromptIssue[] => {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    const key = `${issue.type}:${issue.start}:${issue.end}:${issue.fix?.replacement ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const calculatePromptHealth = (issues: PromptIssue[], prompt?: string): { health: number; breakdown: PromptHealthBreakdown } => {
  const breakdown: PromptHealthBreakdown = {
    conflicts: issues.filter((issue) => issue.type === 'conflict').length,
    duplicates: issues.filter((issue) => issue.type === 'duplicate').length,
    weak: issues.filter((issue) => issue.type === 'weak').length,
    illogical: issues.filter((issue) => issue.type === 'illogical').length,
    cinematic: 0,
    completeness: 0,
  };

  // Issue penalty (more granular)
  const issuePenalty =
    breakdown.conflicts * 18 +
    breakdown.illogical * 16 +
    breakdown.duplicates * 9 +
    breakdown.weak * 6;

  // Structure bonus if prompt is provided
  let structureBonus = 0;
  let structurePenalty = 0;
  if (prompt) {
    const structure = scorePromptStructure(prompt);
    breakdown.cinematic = structure.cinematic;
    breakdown.completeness = structure.completeness;
    
    // Bonus for completeness (max +15)
    structureBonus = Math.min(15, structure.completeness * 3);
    
    // If the prompt is short or mostly non-Latin (e.g. Arabic input not yet refined),
    // give a neutral score instead of penalizing harshly
    const latinChars = (prompt.match(/[a-zA-Z]/g) || []).length;
    const totalChars = prompt.replace(/\s/g, '').length;
    const isNonLatin = totalChars > 0 && latinChars / totalChars < 0.3;
    
    if (isNonLatin) {
      // Non-Latin text: give neutral score, don't penalize for missing English keywords
      const raw = Math.max(50, 100 - issuePenalty);
      return { health: Math.max(0, Math.min(100, Math.round(raw))), breakdown };
    }
    
    // Penalty for missing essential elements (subject, lighting, camera)
    const hasSubject = /subject|character|person|man|woman|girl|boy/i.test(prompt);
    const hasLighting = /light|lighting|lit|illuminat/i.test(prompt);
    const hasCamera = /camera|lens|shot|mm\b|close-up|wide|medium/i.test(prompt);
    const hasComposition = /composition|rule of|framing|centered|thirds/i.test(prompt);
    const hasMood = /mood|atmosphere|tone|dramatic|cinematic|ethereal/i.test(prompt);
    
    if (!hasSubject) structurePenalty += 8;
    if (!hasLighting) structurePenalty += 5;
    if (!hasCamera) structurePenalty += 4;
    if (!hasComposition) structurePenalty += 3;
    if (!hasMood) structurePenalty += 2;
    
    // Penalty for very short prompts
    const wordCount = prompt.trim().split(/\s+/).length;
    if (wordCount < 8) structurePenalty += 12;
    else if (wordCount < 15) structurePenalty += 5;
    
    // Penalty for repetitive words
    const words = prompt.toLowerCase().split(/\s+/);
    const wordFreq = new Map<string, number>();
    words.forEach(w => { if (w.length > 3) wordFreq.set(w, (wordFreq.get(w) || 0) + 1); });
    const repeats = [...wordFreq.values()].filter(c => c > 2).length;
    structurePenalty += repeats * 4;
  }

  const raw = 100 - issuePenalty - structurePenalty + structureBonus;
  return {
    health: Math.max(0, Math.min(100, Math.round(raw))),
    breakdown,
  };
};

const scorePromptStructure = (prompt: string): Pick<PromptHealthBreakdown, 'cinematic' | 'completeness'> => {
  const cinematicSignals = [
    /cinematic|film|movie/i, /composition|framing/i, /depth of field|bokeh|dof/i,
    /grading|color\s*temp|kelvin/i, /dramatic|mood|atmosphere/i,
    /lens\s*flare|volumetric|haze/i, /f[\/-]\d/i,
  ];
  const completenessSignals = [
    /scene|environment|location|background/i,
    /camera|lens|shot|mm\b/i,
    /light|lighting|illuminat|shadow/i,
    /character|subject|person|figure/i,
    /composition|framing|angle|perspective/i,
    /mood|atmosphere|tone/i,
    /style|render|quality/i,
    /color|palette|grade/i,
  ];

  const cinematic = cinematicSignals.reduce((acc, regex) => acc + (regex.test(prompt) ? 1 : 0), 0);
  const completeness = completenessSignals.reduce((acc, regex) => acc + (regex.test(prompt) ? 1 : 0), 0);

  return { cinematic, completeness };
};

const addLocalInspectorRules = (prompt: string): PromptIssue[] => {
  const issues: PromptIssue[] = [];
  const lower = prompt.toLowerCase();
  const hasGoldenHour = /golden hour/i.test(prompt);
  const hasDaylight5500 = /(daylight\s*5500k|5500k\s*daylight|5500k)/i.test(prompt);
  const hasDaylight = /daylight/i.test(prompt);
  const hasLowKey = /low\s*key lighting/i.test(prompt);
  const hasBrightSunlight = /bright sunlight/i.test(prompt);
  const hasNight = /night/i.test(prompt);
  const hasCloseUp = /close\s*-?up/i.test(prompt);
  const hasWideShot = /wide shot/i.test(prompt);
  const hasOverShoulder = /over\s*shoulder/i.test(prompt);
  const hasFrontShot = /front shot/i.test(prompt);
  const pushIssue = (needle: string, type: PromptIssue['type'], message: string, replacement: string) => {
    const start = lower.indexOf(needle.toLowerCase());
    if (start === -1) return;
    issues.push({
      id: `inspector-${type}-${start}-${needle.length}`,
      type,
      message,
      start,
      end: start + needle.length,
      fix: { replacement },
    });
  };

  if (hasGoldenHour && hasDaylight5500) {
    pushIssue('5500k', 'conflict', 'Lighting conflict: golden hour is warmer than 5500K daylight.', '3200K');
  }

  if (hasGoldenHour && hasDaylight) {
    pushIssue('daylight', 'conflict', 'Golden hour usually conflicts with neutral daylight wording.', 'warm sunset light');
  }

  if (hasLowKey && hasBrightSunlight) {
    pushIssue('bright sunlight', 'conflict', 'Low-key lighting conflicts with bright sunlight.', 'controlled key light');
  }

  if (hasNight && hasBrightSunlight) {
    pushIssue('bright sunlight', 'conflict', 'Night scene conflicts with bright sunlight.', 'moonlight glow');
  }

  if (hasCloseUp && hasWideShot) {
    pushIssue('wide shot', 'conflict', 'Camera conflict: close-up and wide shot in the same beat.', 'medium close-up shot');
  }

  if (hasOverShoulder && hasFrontShot) {
    pushIssue('front shot', 'illogical', 'Camera angle conflict: over-shoulder and front shot clash.', 'over-shoulder angle');
  }

  if (/50mm lens/gi.test(prompt)) {
    const all = [...prompt.matchAll(/50mm lens/gi)];
    if (all.length > 1) {
      const second = all[1];
      issues.push({
        id: `inspector-duplicate-${second.index}`,
        type: 'duplicate',
        message: 'Duplicate parameter: 50mm lens appears twice.',
        start: second.index ?? 0,
        end: (second.index ?? 0) + second[0].length,
        fix: { replacement: 'balanced lens setup' },
      });
    }
  }

  return issues;
};

const withValidHighlights = (prompt: string, issues: PromptIssue[]): PromptIssue[] => {
  const validRegexes = [/\b50\s?mm lens\b/gi, /\b1\/125 shutter\b/gi, /\beye-?level\b/gi, /\b16:9\b/gi];
  const additions: PromptIssue[] = [];

  validRegexes.forEach((regex, index) => {
    let match: RegExpExecArray | null;
    while ((match = regex.exec(prompt)) !== null) {
      additions.push({
        id: `valid-${index}-${match.index}`,
        type: 'valid',
        message: 'Balanced setting detected.',
        start: match.index,
        end: match.index + match[0].length,
      });
    }
  });

  return uniqueIssues([...issues, ...additions]).sort((a, b) => a.start - b.start);
};

const firstMatch = (prompt: string, regex: RegExp, fallback = ''): string => {
  const hit = prompt.match(regex);
  return hit?.[0]?.trim() || fallback;
};

const extractPromptSegments = (prompt: string): Record<string, string> => ({
  SUBJECT: firstMatch(prompt, /(?:subject|hero|main subject|featuring)[-:]?\s*([^.,]+)/i, firstMatch(prompt, /^[^.,]+/)),
  'CHARACTER DETAILS': firstMatch(prompt, /(?:character|wearing|age|expression|pose)[-:]?\s*([^.,]+)/i),
  ACTION: firstMatch(prompt, /(?:action|doing|running|standing|walking|jumping|looking)[-:]?\s*([^.,]+)/i),
  ENVIRONMENT: firstMatch(prompt, /(?:environment|scene|background|location|in a|at a)[-:]?\s*([^.,]+)/i),
  'SHOT TYPE': firstMatch(prompt, /(extreme close-up|close-up|medium shot|wide shot|full body|portrait framing|tight portrait)/i),
  'CAMERA SETTINGS': firstMatch(prompt, /(iso\s*\d+|shutter\s*\d+\/\d+|f\/\d+(?:\.\d+)?)/i),
  'LENS TYPE': firstMatch(prompt, /(macro lens|wide angle|telephoto|\d+\s?mm lens|anamorphic)/i),
  LIGHTING: firstMatch(prompt, /(low key lighting|soft ambient light|harsh spotlight|natural daylight|torch light|rim light|golden hour)/i),
  'COLOR TEMPERATURE': firstMatch(prompt, /(\d{4}\s?K|\d{4}k|warm|cool|daylight|tungsten)/i),
  COMPOSITION: firstMatch(prompt, /(centered composition|dynamic angle|rule of thirds|symmetrical framing|leading lines)/i),
  'MOOD / ATMOSPHERE': firstMatch(prompt, /(moody|dramatic|mystical|foggy|atmospheric|tense|calm)/i),
  'RENDER STYLE': firstMatch(prompt, /(photorealistic|anime|cartoon|3d render|oil painting|cinematic)/i),
  'QUALITY TAGS': firstMatch(prompt, /(8k|4k|ultra detailed|high detail|masterpiece|best quality)/i),
  'TECHNICAL PARAMETERS': firstMatch(prompt, /(--ar\s*\S+|--stylize\s*\d+|seed\s*\d+|steps\s*\d+)/i),
  'EXTRA MODIFIERS': firstMatch(prompt, /(volumetric fog|depth of field|bokeh|film grain|ray traced|global illumination)/i),
});


const findConflictPair = (promptLower: string, group: string[], label: string): AdvancedConflict | null => {
  const hits = group.filter((term) => promptLower.includes(term));
  if (hits.length < 2) return null;
  return {
    conflictType: label,
    detectedElements: hits.slice(0, 4),
    explanation: `${label} conflict: ${hits.join(', ')} should not coexist in one coherent prompt beat.`,
    severity: Math.min(10, 4 + hits.length),
  };

};

const SHOT_TYPE_REGEX = /\b(close-up|medium shot|wide shot|full body|tight portrait framing)\b/gi;
const LIGHTING_REGEX = /\b(low key lighting|soft ambient light|harsh spotlight|natural daylight|torch light)\b/gi;
const COLOR_TEMP_REGEX = /\b(2500k|3200k|5500k|7500k)\b/gi;
const STYLE_REGEX = /\b(photorealistic|anime|cartoon|3d render|oil painting)\b/gi;

const detectAdvancedConflicts = (prompt: string): AdvancedConflict[] => {
  const lower = prompt.toLowerCase();
  const buckets: Array<{ label: string; group: string[] }> = [
    { label: 'SHOT TYPE conflicts', group: ['close-up', 'medium shot', 'wide shot', 'full body', 'tight portrait framing'] },
    { label: 'LIGHTING conflicts', group: ['low key lighting', 'soft ambient light', 'harsh spotlight', 'natural daylight', 'torch light'] },
    { label: 'COLOR TEMPERATURE conflicts', group: ['2500k', '3200k', '5500k', '7500k'] },
    { label: 'STYLE conflicts', group: ['photorealistic', 'anime', 'cartoon', '3d render', 'oil painting'] },
    { label: 'ENVIRONMENT conflicts', group: ['indoor temple', 'outdoor desert', 'underwater', 'space'] },
    { label: 'CAMERA conflicts', group: ['macro lens', 'wide angle', 'telephoto'] },
    { label: 'COMPOSITION conflicts', group: ['centered composition', 'dynamic angle', 'rule of thirds', 'symmetrical framing'] },
  ];

  const conflicts = buckets
    .map((bucket) => findConflictPair(lower, bucket.group, bucket.label))
    .filter((item): item is AdvancedConflict => Boolean(item));

  if (/([^\s]{1,2}\s){8,}/.test(prompt)) {
    conflicts.push({
      conflictType: 'Corrupted text / broken tokens',
      detectedElements: ['token fragmentation'],
      explanation: 'Detected potential broken tokens or damaged grammar fragment.',
      severity: 7,
    });
  }

  return conflicts;
};

const dedupeConflicts = (items: AdvancedConflict[]): AdvancedConflict[] => {
  const map = new Map<string, AdvancedConflict>();
  items.forEach((item) => {
    const key = `${item.conflictType.toLowerCase()}::${item.detectedElements.join('|').toLowerCase()}`;
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

const findLocationFromTerms = (prompt: string, terms: string[]): { start: number; end: number } | undefined => {
  const lower = prompt.toLowerCase();
  const indexes = terms
    .map((term) => {
      const clean = term.toLowerCase();
      const index = lower.indexOf(clean);
      return index >= 0 ? { index, length: clean.length } : null;
    })
    .filter((item): item is { index: number; length: number } => Boolean(item));

  if (!indexes.length) return undefined;
  const start = Math.min(...indexes.map((item) => item.index));
  const end = Math.max(...indexes.map((item) => item.index + item.length));
  return { start, end };
};

const detectSemanticConflictsLocal = (prompt: string): AdvancedConflict[] => {
  const lower = prompt.toLowerCase();
  const rules: Array<{ pair: [RegExp, RegExp]; type: string; explanation: string; severity: number; detected: string[] }> = [
    {
      pair: [/sunrise|dawn/, /midnight|dead of night|deep night/],
      type: 'Time logic conflict',
      explanation: 'Sunrise and midnight cannot happen in the same single coherent shot.',
      severity: 8,
      detected: ['sunrise', 'midnight'],
    },
    {
      pair: [/bright sunlight|harsh sunlight|direct sun/, /night scene|moonlit|midnight|night/],
      type: 'Lighting vs time conflict',
      explanation: 'Bright sunlight conflicts with a night scene setup.',
      severity: 8,
      detected: ['bright sunlight', 'night scene'],
    },
    {
      pair: [/underwater|submerged/, /desert|sand dunes|arid/],
      type: 'Environment physics conflict',
      explanation: 'Underwater and desert conditions conflict in the same environment context.',
      severity: 9,
      detected: ['underwater', 'desert'],
    },
    {
      pair: [/macro shot|extreme close-up|close-up macro/, /aerial view|bird.?s-eye|drone shot/],
      type: 'Camera grammar conflict',
      explanation: 'Macro close framing conflicts with aerial wide framing in one shot declaration.',
      severity: 7,
      detected: ['macro shot', 'aerial view'],
    },
    {
      pair: [/heavy rain|thunderstorm|storm/, /clear sky|cloudless sky/],
      type: 'Weather conflict',
      explanation: 'Heavy rain conflicts with clear sky in the same moment.',
      severity: 7,
      detected: ['heavy rain', 'clear sky'],
    },
  ];

  return rules
    .filter((rule) => rule.pair[0].test(lower) && rule.pair[1].test(lower))
    .map((rule) => ({
      conflictType: rule.type,
      detectedElements: rule.detected,
      explanation: rule.explanation,
      severity: rule.severity,
      location: findLocationFromTerms(prompt, rule.detected),
      suggestedFix: rule.type === 'Camera grammar conflict' ? 'Choose one camera grammar: medium shot eye-level.' : 'Keep one coherent scene state for this axis.',
      layer: 'semantic',
    }));
};

const detectSemanticConflictsWithAI = async (prompt: string): Promise<AdvancedConflict[]> => {
  if (!prompt.trim()) return detectSemanticConflictsLocal(prompt);

  try {
    const aiResult = await callPromptAI('analyze', prompt);
    if (!aiResult || !Array.isArray(aiResult.issues)) return detectSemanticConflictsLocal(prompt);

    const aiConflicts: AdvancedConflict[] = aiResult.issues
      .filter((item: any) => item.type === 'conflict' || item.type === 'illogical')
      .map((item: any) => ({
        conflictType: item.type === 'illogical' ? 'Illogical combination' : 'Semantic conflict',
        detectedElements: [item.text_span || ''],
        explanation: item.reason || 'Conflict detected by AI',
        severity: 7,
        location: findLocationFromTerms(prompt, [item.text_span || '']),
        suggestedFix: item.suggested_fix || 'Fix this conflict.',
        layer: 'semantic' as const,
      }));

    return dedupeConflicts([...detectSemanticConflictsLocal(prompt), ...aiConflicts]);
  } catch {
    return detectSemanticConflictsLocal(prompt);
  }
};


const analyzeSemanticStructureWithGrok = async (prompt: string): Promise<GrokSemanticResponse | null> => {
  if (!prompt.trim()) return null;
  // Now handled by the unified edge function in analyzePromptWithGrok
  return null;
};

const buildConflictScore = (conflicts: AdvancedConflict[]): ConflictScore => {
  const count = conflicts.length;
  const severityTotal = conflicts.reduce((acc, item) => acc + item.severity, 0);
  if (count === 0) return { count, severityTotal, level: 'Safe Prompt' };
  if (count === 1) return { count, severityTotal, level: 'Minor Warning' };
  if (count === 2) return { count, severityTotal, level: 'Needs Fix' };
  return { count, severityTotal, level: 'Broken Prompt' };
};

const mapAdvancedConflictsToIssues = (prompt: string, conflicts: AdvancedConflict[]): PromptIssue[] => {
  return conflicts.map((conflict, index) => {
    const loc = conflict.location || findLocationFromTerms(prompt, conflict.detectedElements) || { start: 0, end: Math.min(prompt.length, 1) };
    return {
      id: `semantic-conflict-${loc.start}-${loc.end}-${index}`,
      type: 'conflict',
      message: `${conflict.conflictType}: ${conflict.explanation}`,
      reason: conflict.explanation,
      severity: conflict.severity >= 7 ? 'high' : 'medium',
      start: Math.max(0, loc.start),
      end: Math.max(Math.max(0, loc.start) + 1, Math.min(prompt.length, loc.end)),
      location: {
        start: Math.max(0, loc.start),
        end: Math.max(Math.max(0, loc.start) + 1, Math.min(prompt.length, loc.end)),
      },
      suggestedFix: conflict.suggestedFix || 'coherent cinematic directive',
      fix: {
        replacement: conflict.suggestedFix || 'coherent cinematic directive',
      },
    };
  });
};

const mapPhysicsIssuesToPromptIssues = (physics: PhysicsIssue[]): PromptIssue[] => {
  return physics.map((issue, index) => ({
    id: `${issue.id}-${index}`,
    type: issue.severity === 'impossible' ? 'conflict' : 'illogical',
    message: `[${issue.category}] ${issue.explanation}`,
    reason: issue.explanation,
    severity: issue.severity === 'impossible' ? 'high' : 'medium',
    start: issue.start,
    end: issue.end,
    location: { start: issue.start, end: issue.end },
    suggestedFix: issue.suggestion,
    fix: { replacement: issue.suggestion },
  }));
};


const mapSemanticPayloadToIssues = (prompt: string, payload: GrokSemanticResponse | null): PromptIssue[] => {
  if (!payload) return [];

  const issues: PromptIssue[] = [];

  (payload.conflicts || []).forEach((item, index) => {
    const text = String(item.text || '').trim();
    const reason = String(item.explanation || `${text} conflicts with ${item.conflictingWith || 'another directive'}`).trim();
    const suggestion = String(item.suggestion || '').trim() || 'Use one coherent cinematic directive.';
    const loc = item.location?.start !== undefined && item.location?.end !== undefined
      ? { start: Number(item.location.start) || 0, end: Number(item.location.end) || 0 }
      : findLocationFromTerms(prompt, [text, String(item.conflictingWith || '')].filter(Boolean));
    const start = Math.max(0, Math.min(prompt.length, loc?.start ?? 0));
    const end = Math.max(start + 1, Math.min(prompt.length, loc?.end ?? (start + Math.max(1, text.length))));

    issues.push({
      id: `semantic-ai-conflict-${start}-${end}-${index}`,
      type: 'conflict',
      message: reason,
      reason,
      severity: 'high',
      start,
      end,
      location: { start, end },
      suggestedFix: suggestion,
      fix: { replacement: suggestion },
    });
  });

  (payload.duplicates || []).forEach((item, index) => {
    const text = String(item.text || '').trim();
    if (!text) return;

    const location = item.locations?.[1] || item.locations?.[0];
    const fallbackStart = prompt.toLowerCase().indexOf(text.toLowerCase());
    const start = Math.max(0, Math.min(prompt.length, Number(location?.start) || fallbackStart));
    if (start < 0) return;

    const end = Math.max(start + 1, Math.min(prompt.length, Number(location?.end) || (start + text.length)));

    issues.push({
      id: `semantic-ai-duplicate-${start}-${end}-${index}`,
      type: 'duplicate',
      message: 'Duplicate directive detected by Grok semantic pass.',
      reason: 'Grok semantic pass found repeated cinematic directives.',
      severity: 'medium',
      start,
      end,
      location: { start, end },
      suggestedFix: 'Remove duplicated directive.',
      fix: { replacement: '' },
    });
  });

  (payload.valid || []).forEach((item, index) => {
    const text = String(item.text || '').trim();
    if (!text) return;

    const range = findLocationFromTerms(prompt, [text]);
    if (!range) return;

    issues.push({
      id: `semantic-ai-valid-${range.start}-${range.end}-${index}`,
      type: 'valid',
      message: 'Semantically valid cinematic segment.',
      start: range.start,
      end: range.end,
    });
  });

  return issues;
};

const scoreAdvancedPrompt = (prompt: string, conflicts: AdvancedConflict[], issues: PromptIssue[]) => {
  const sentenceCount = prompt.split(/[.!?]/).map((t) => t.trim()).filter(Boolean).length;
  const wordCount = prompt.split(/\s+/).filter(Boolean).length;
  const hasCamera = /(lens|camera|shot|f\/|iso|shutter)/i.test(prompt);
  const hasLighting = /(light|lighting|key|rim|daylight|golden hour)/i.test(prompt);
  const hasComposition = /(composition|rule of thirds|symmetrical|framing)/i.test(prompt);
  const hasMood = /(mood|atmosphere|dramatic|moody|fog)/i.test(prompt);

  const clarity = Math.max(4, 20 - conflicts.length * 3 - Math.max(0, sentenceCount - 6));
  const structure = Math.max(4, 7 + Math.min(8, sentenceCount));
  const consistency = Math.max(1, 15 - conflicts.reduce((acc, c) => acc + (c.severity >= 8 ? 3 : 2), 0));
  const visualRichness = Math.max(4, Math.min(15, (hasMood ? 5 : 2) + (wordCount > 35 ? 5 : 3) + (hasLighting ? 3 : 1) + (hasComposition ? 2 : 1)));
  const cinematicQuality = Math.max(4, Math.min(15, (hasCamera ? 6 : 2) + (hasLighting ? 5 : 2) + (hasComposition ? 4 : 1)));
  const technicalAccuracy = Math.max(2, Math.min(10, (hasCamera ? 4 : 1) + (hasLighting ? 3 : 1) + (/\d{4}\s?k/i.test(prompt) ? 3 : 1)));
  const cleanliness = Math.max(1, 10 - issues.filter((i) => i.type !== 'valid').length);

  const total = clarity + structure + consistency + visualRichness + cinematicQuality + technicalAccuracy + cleanliness;
  return {
    total: Math.max(0, Math.min(100, total)),
    breakdown: { clarity, structure, consistency, visualRichness, cinematicQuality, technicalAccuracy, cleanliness },
  };

};

const buildSegmentHealth = (segments: Record<string, string>, conflicts: AdvancedConflict[]): PromptSegmentHealth[] => {
  const hasConflict = (label: string) => conflicts.some((c) => c.conflictType.toLowerCase().includes(label.toLowerCase()));
  const mk = (segment: PromptSegmentHealth['segment'], key: string, label: string, fix: string): PromptSegmentHealth => {
    const value = segments[key]?.trim();
    if (!value) return { segment, status: 'broken', detected: 'Missing', suggestedFix: fix };
    if (hasConflict(label)) return { segment, status: 'conflict', detected: value, suggestedFix: fix };
    return { segment, status: 'valid', detected: value, suggestedFix: fix };
  };

  return [
    mk('SUBJECT', 'SUBJECT', 'subject', 'Define one clear main subject.'),
    mk('SHOT TYPE', 'SHOT TYPE', 'SHOT TYPE', 'Keep a single coherent shot type.'),
    mk('LIGHTING', 'LIGHTING', 'LIGHTING', 'Use one primary lighting setup with optional fill/rim.'),
    mk('COLOR TEMPERATURE', 'COLOR TEMPERATURE', 'COLOR TEMPERATURE', 'Use one temperature anchor such as 3200K or 5500K.'),
    mk('CAMERA', 'LENS TYPE', 'CAMERA', 'Pick one lens family appropriate for the shot.'),
    mk('ENVIRONMENT', 'ENVIRONMENT', 'ENVIRONMENT', 'Keep one consistent location context.'),
    mk('COMPOSITION', 'COMPOSITION', 'COMPOSITION', 'Use one dominant composition strategy.'),
    mk('STYLE', 'RENDER STYLE', 'STYLE', 'Keep one core style family.'),
    mk('QUALITY TAGS', 'QUALITY TAGS', 'quality', 'Use concise quality tags without repetition.'),
  ];
};

const ensureCinematicLayer = (prompt: string): string => {
  const additions: string[] = [];
  if (!/(\d+\s?mm lens|anamorphic|macro lens|wide angle|telephoto)/i.test(prompt)) additions.push('50mm cinema lens');
  if (!/(depth of field|bokeh)/i.test(prompt)) additions.push('shallow depth of field');
  if (!/(rim light|key light|soft ambient|volumetric|golden hour)/i.test(prompt)) additions.push('motivated cinematic key light with soft rim');
  if (!/(atmosphere|fog|mist|haze)/i.test(prompt)) additions.push('subtle atmospheric haze');
  if (!/(composition|rule of thirds|symmetrical|leading lines)/i.test(prompt)) additions.push('rule of thirds composition');

  if (!additions.length) return prompt.trim();
  const base = prompt.trim().replace(/\s+/g, ' ');
  return `${base}, ${additions.join(', ')}`;
};

const buildAdvancedPromptReport = (prompt: string, issues: PromptIssue[]): AdvancedPromptReport => {
  const segments = extractPromptSegments(prompt);
  const conflicts = detectAdvancedConflicts(prompt);
  const score = scoreAdvancedPrompt(prompt, conflicts, issues);
  const versionA = resolveAllPromptIssues(prompt);
  const versionB = ensureCinematicLayer(versionA)
    .replace(/\s+/g, ' ')
    .replace(/,\s*,/g, ', ')
    .trim();
  const cinematicVersion = `${versionB}, cinematic lighting logic, coherent camera grammar`;
  const finalCleanPrompt = resolveAllPromptIssues(cinematicVersion).replace(/\s+/g, ' ').trim();

  return {
    segments,
    advancedConflicts: conflicts,
    promptScore: score.total,
    scoreBreakdown: score.breakdown,
    scoreReason: 'Score drops when conflicting shot/lighting/style instructions appear together or when essential cinematic metadata is missing.',
    segmentHealth: buildSegmentHealth(segments, conflicts),
    versionA,
    versionB,
    cinematicVersion,
    suggestions: [
      'Choose one primary shot type (e.g., medium close-up) and remove conflicting framing terms.',
      'Keep one dominant color temperature anchor and align light sources to it.',
      'Select one render style family to avoid style collisions.',
      'Add one clear lens declaration (e.g., 50mm or 85mm) for stronger camera intent.',
      'Use concise modifiers and remove repeated quality tags.',
    ],
    finalCleanPrompt,
  };

};


export const analyzePromptWithGrok = async (prompt: string, canvasSubject?: string | null): Promise<GrokPromptAnalysis> => {
  const normalizedSubject = normalizeSubject(canvasSubject, null);
  const cacheKey = `${prompt}::${normalizedSubject ?? ''}`;
  const cached = analysisCache.get(cacheKey);
  if (cached) return cached;

  const baseIssues = analyzeInlinePromptIssues(prompt);
  const localReport = buildAdvancedPromptReport(prompt, baseIssues);
  const physicsIssues = mapPhysicsIssuesToPromptIssues(analyzePromptPhysics(prompt));

  try {
    const safeCanvasSubject = normalizedSubject;
    const aiResult = await callPromptAI('analyze', prompt, safeCanvasSubject);

    let aiMapped: PromptIssue[] = [];
    if (aiResult && Array.isArray(aiResult.issues)) {
      aiMapped = mapGrokIssuesToPromptIssues(prompt, aiResult.issues as GrokIssue[]);
    }

    // Also map valid_parts from AI
    if (aiResult && Array.isArray(aiResult.valid_parts)) {
      aiResult.valid_parts.forEach((text: string, index: number) => {
        const issue = buildIssueFromText(prompt, text, 'valid', 'Valid cinematic segment.', undefined, index);
        if (issue) aiMapped.push(issue);
      });
    }

    // Use local semantic conflict detection only — AI analyze call above already covers conflicts
    const semanticConflicts = detectSemanticConflictsLocal(prompt);
    const semanticIssues = mapAdvancedConflictsToIssues(prompt, semanticConflicts);

    const issues = withValidHighlights(prompt, uniqueIssues([
      ...baseIssues,
      ...addLocalInspectorRules(prompt),
      ...aiMapped,
      ...semanticIssues,
      ...physicsIssues,
    ]));
    const { health, breakdown } = calculatePromptHealth(issues, prompt);
    const result = { issues, health, breakdown, advancedReport: localReport, raw: aiResult };
    analysisCache.set(cacheKey, result);
    return result;
  } catch {
    const issues = withValidHighlights(prompt, uniqueIssues([
      ...baseIssues,
      ...addLocalInspectorRules(prompt),
      ...physicsIssues,
    ]));
    const { health, breakdown } = calculatePromptHealth(issues, prompt);
    const result = { issues, health, breakdown, advancedReport: localReport };
    analysisCache.set(cacheKey, result);
    return result;
  }
};


const pickFirst = (prompt: string, candidates: string[], fallback: string): string => {
  const lower = prompt.toLowerCase();
  const hit = candidates.find((item) => lower.includes(item));
  return hit || fallback;
};

const parseInitialPromptLocal = (prompt: string): SceneUnderstanding => {
  const text = prompt.trim();
  if (!text) {
    return {
      subject: 'main subject',
      action: 'standing with natural pose',
      environment: 'cinematic environment',
      lighting: 'soft cinematic key light',
      weather: 'clear weather',
      mood: 'immersive cinematic mood',
      camera: 'eye-level camera',
      shotScale: 'medium shot',
      time: 'golden hour',
      style: 'cinematic photorealistic',
      composition: 'rule of thirds',
    };
  }

  const isTemple = /معبد|temple/i.test(text);
  const isWarrior = /محارب|فارس|warrior|knight/i.test(text);
  const isDark = /مظلم|dark|ليل|night/i.test(text);
  const isDramatic = /درامي|dramatic|epic|ملحمي/i.test(text);
  const isFarAway = /من بعيد|بعيد|far away|from a?far|distant|from a distance/i.test(text);
  const isCloseUp = /قريب|close[- ]?up|من قريب|وجه/i.test(text);
  const isZombie = /زومبي|زمبي|zombie/i.test(text);
  const isLaughing = /بيضحك|ضاحك|يضحك|laughing|smiling|بيبتسم/i.test(text);
  const isCrying = /بيعيط|يبكي|crying|weeping/i.test(text);
  const isAngry = /غاضب|زعلان|angry|furious/i.test(text);
  const isScary = /مخيف|scary|terrifying|مرعب/i.test(text);

  const weather = pickFirst(text, ['heavy rain', 'rain', 'thunderstorm', 'snow', 'foggy', 'mist', 'clear sky', 'clear weather', 'مطر', 'ثلج', 'ضباب'], 'clear weather');

  // Detect expression from text
  let detectedExpression = '';
  if (isLaughing) detectedExpression = 'laughing';
  else if (isCrying) detectedExpression = 'crying';
  else if (isAngry) detectedExpression = 'angry';
  else if (isScary) detectedExpression = 'menacing';
  else if (/حزين|sad/i.test(text)) detectedExpression = 'sad';
  else if (/خايف|scared|afraid/i.test(text)) detectedExpression = 'scared';
  else if (/مبتسم|happy|سعيد/i.test(text)) detectedExpression = 'happy';

  // Detect shot scale from distance keywords
  let shotScale = 'medium shot';
  let camera = isDramatic ? 'low angle shot' : 'medium close-up eye-level';
  if (isFarAway) {
    shotScale = 'wide shot';
    camera = 'wide shot';
  } else if (isCloseUp) {
    shotScale = 'close-up';
    camera = 'close-up shot';
  }

  // Detect subject
  let subject = firstMatch(
    text,
    /(?:subject|hero|main|تصميم لي?|عايز)\s*:?\s*([^.,\n]+)/i,
    isWarrior ? 'warrior' : isZombie ? 'zombie character' : firstMatch(text, /^[^.,]+/, 'main subject')
  );

  // Also detect subject from Arabic pattern "شخص X" or "شخصية X"
  const personMatch = text.match(/(?:شخص|شخصية|person|character)\s+([^\s.,]+(?:\s+[^\s.,]+)?)/i);
  if (personMatch) subject = personMatch[0].trim();
  if (isZombie && !/zombie|زومبي|زمبي/i.test(subject)) subject = `zombie ${subject}`.trim();

  // Detect action from expression/behavior
  let action = firstMatch(text, /(?:action|doing|running|walking|standing|looking)[:-]?\s*([^.,]+)/i, 'natural cinematic action');
  if (detectedExpression === 'laughing') action = 'laughing expressively';
  else if (detectedExpression === 'crying') action = 'crying with emotion';
  else if (detectedExpression === 'angry') action = 'showing anger';
  else if (/واقف|standing/i.test(text)) action = 'standing in a heroic pose';

  return {
    subject,
    action,
    environment: firstMatch(text, /(?:environment|scene|location|background|in|at|في|بمكان)[:-]?\s*([^.,]+)/i, isTemple ? 'ancient temple' : 'detailed cinematic location'),
    lighting: isDark ? 'dark torch light with cinematic contrast' : pickFirst(text, ['golden hour', 'soft ambient light', 'low key lighting', 'natural daylight', 'volumetric light', 'اضاءة'], 'cinematic key light with soft fill'),
    weather,
    mood: isDramatic || isDark || isScary ? 'dramatic' : pickFirst(text, ['dramatic', 'moody', 'calm', 'mystical', 'tense', 'atmospheric'], 'cinematic dramatic mood'),
    camera: pickFirst(text, ['close-up', 'medium shot', 'wide shot', 'eye-level', 'top-down', 'low angle'], camera),
    shotScale: pickFirst(text, ['extreme close-up', 'close-up', 'medium shot', 'wide shot', 'full body'], shotScale),
    time: pickFirst(text, ['sunrise', 'morning', 'noon', 'sunset', 'golden hour', 'night', 'blue hour', 'ليل', 'نهار'], isDark ? 'night' : 'golden hour'),
    style: pickFirst(text, ['photorealistic', 'cinematic', 'anime', 'oil painting', '3d render', 'cartoon'], 'cinematic photorealistic'),
    composition: pickFirst(text, ['rule of thirds', 'centered composition', 'symmetrical framing', 'dynamic angle', 'leading lines'], isFarAway ? 'centered composition' : 'rule of thirds'),
  };

};


const buildProfessionalPromptFromScene = (scene: SceneUnderstanding): string => {
  return [
    `MAIN SUBJECT: ${scene.subject}`,
    `ACTION: ${scene.action}`,
    `ENVIRONMENT: ${scene.environment}`,
    `TIME OF DAY: ${scene.time}`,
    `WEATHER: ${scene.weather}`,
    `LIGHTING: ${scene.lighting}`,
    `MOOD: ${scene.mood}`,
    `CAMERA: ${scene.camera}`,
    `SHOT SCALE: ${scene.shotScale}`,
    'LENS: 50mm cinematic lens',
    `COMPOSITION: ${scene.composition || 'rule of thirds'}, clean subject separation`,
    'DETAIL LEVEL: high detail with natural textures',
    `STYLE: ${scene.style}`,
    'RENDER QUALITY: photoreal, global illumination, sharp focus',
  ].join('. ');
};

const autoRepairFromConflicts = (basePrompt: string, conflicts: AdvancedConflict[]): string => {
  let next = basePrompt;

  const hasConflictType = (label: string) => conflicts.some((c) => c.conflictType.includes(label));

  if (hasConflictType('SHOT TYPE')) {
    next = next.replace(SHOT_TYPE_REGEX, 'medium close-up shot');
  }

  if (hasConflictType('LIGHTING')) {
    next = next.replace(LIGHTING_REGEX, 'cinematic key light with soft fill and subtle rim light');
  }

  if (hasConflictType('COLOR TEMPERATURE')) {
    next = next.replace(COLOR_TEMP_REGEX, '3200K');
  }

  if (hasConflictType('STYLE')) {
    next = next.replace(STYLE_REGEX, 'cinematic photorealistic');
  }

  if (hasConflictType('Camera grammar conflict') || hasConflictType('SHOT TYPE')) {
    const heroDriven = /portrait|character|subject|hero/i.test(next);
    const compositionWide = /establishing|environment|landscape|wide/i.test(next);
    const resolvedShot = compositionWide ? 'wide shot' : heroDriven ? 'medium close-up shot' : 'medium shot';
    next = next
      .replace(/SHOT SCALE:\s*[^.]+/i, `SHOT SCALE: ${resolvedShot}`)
      .replace(SHOT_TYPE_REGEX, resolvedShot);
  }

  return resolveAllPromptIssues(next).replace(/\s+/g, ' ').trim();
};

const cinematicUpgradePrompt = (prompt: string): string => {
  const additions: string[] = [];
  if (!/cinematic lighting/i.test(prompt)) additions.push('cinematic lighting');
  if (!/volumetric light|volumetric fog/i.test(prompt)) additions.push('volumetric light');
  if (!/depth of field|bokeh/i.test(prompt)) additions.push('depth of field');
  if (!/dramatic shadows/i.test(prompt)) additions.push('dramatic shadows');
  if (!/atmospheric particles|dust particles|mist particles/i.test(prompt)) additions.push('atmospheric particles');
  if (!/film lens|\d+mm/i.test(prompt)) additions.push('film lens');
  if (!additions.length) return prompt;
  return `${prompt}. ${additions.join(', ')}`;
};

const scorePromptForUi = (prompt: string, conflicts: AdvancedConflict[]): number => {
  const clarity = Math.max(5, 20 - conflicts.length * 2);
  const structure = /MAIN SUBJECT:|ACTION:|ENVIRONMENT:/i.test(prompt) ? 20 : 10;
  const visualRichness = /(lighting|mood|composition|detail|environment)/i.test(prompt) ? 20 : 10;
  const compatibility = /(lens|render quality|photoreal|style)/i.test(prompt) ? 20 : 10;
  const cinematic = /(cinematic|depth of field|volumetric|dramatic shadows)/i.test(prompt) ? 20 : 10;
  return Math.max(0, Math.min(100, clarity + structure + visualRichness + compatibility + cinematic));
};

const buildAutoDirector = (scene: SceneUnderstanding): PromptSettingsPreset => {
  const dramatic = /dramatic|moody|dark|tense/i.test(`${scene.mood} ${scene.lighting}`);
  const night = /night|ليل|blue hour/i.test(scene.time);
  return {
    camera: dramatic ? 'low angle shot' : 'eye-level medium shot',
    lighting: night ? 'low key cinematic lighting' : 'soft cinematic daylight',
    style: scene.style,
    composition: dramatic ? 'centered cinematic with negative space' : 'rule of thirds',
    mood: scene.mood,
    lens: dramatic ? '85mm lens' : '50mm lens',
    palette: night ? 'cinematic-blue-hour-soft' : 'cinematic-golden-sunset-soft',
    renderQuality: '4k',
  };
};

const preventPromptConflicts = (scene: SceneUnderstanding): { scene: SceneUnderstanding; warnings: ConflictWarning[] } => {
  const warnings: ConflictWarning[] = [];
  const next = { ...scene };

  if (/close-up/i.test(next.camera) && /full body/i.test(next.camera)) {
    next.camera = 'medium close-up shot';
    warnings.push({ warning: 'Close-up conflicts with full body framing', preventedBy: 'Using medium close-up shot' });
  }

  if (/close-up|extreme close-up/i.test(next.shotScale) && /full body|wide/i.test(next.shotScale)) {
    next.shotScale = /environment|landscape/i.test(next.composition) ? 'wide shot' : 'medium shot';
    warnings.push({ warning: 'Shot scale conflict detected', preventedBy: `Auto Shot Resolver selected ${next.shotScale}` });
  }

  if (/night|ليل/i.test(next.time) && /daylight|sunlight/i.test(next.lighting)) {
    next.lighting = 'moonlit cinematic lighting with soft practicals';
    warnings.push({ warning: 'Night scene conflicts with strong daylight', preventedBy: 'Switched to moonlit cinematic lighting' });
  }

  if (/anime/i.test(next.style) && /photorealistic/i.test(next.style)) {
    next.style = 'cinematic photorealistic';
    warnings.push({ warning: 'Style conflict between anime and photorealistic', preventedBy: 'Kept cinematic photorealistic style' });
  }

  if (/sunrise|dawn/i.test(next.time) && /midnight|deep night/i.test(next.time)) {
    next.time = 'sunrise';
    warnings.push({ warning: 'Sunrise conflicts with midnight', preventedBy: 'Kept sunrise as primary time anchor' });
  }

  if (/underwater/i.test(next.environment) && /desert|dune/i.test(next.environment)) {
    next.environment = 'underwater ancient ruins';
    warnings.push({ warning: 'Underwater conflicts with desert environment', preventedBy: 'Locked environment to underwater ruins' });
  }

  if (/heavy rain|storm/i.test(next.weather) && /clear sky|clear weather/i.test(next.weather)) {
    next.weather = 'heavy rain';
    warnings.push({ warning: 'Heavy rain conflicts with clear sky', preventedBy: 'Kept heavy rain weather state' });
  }

  return { scene: next, warnings };
};

export const buildPromptIntelligence = async (initialPrompt: string): Promise<PromptIntelligenceResult> => {
  const clean = initialPrompt.trim();
  const cached = intelligenceCache.get(clean);
  if (cached) return cached;
  const localScene = parseInitialPromptLocal(clean);
  let scene = localScene;

  // Use local parser only - AI scene extraction is handled by the prompt pipeline
  // This avoids extra AI calls and keeps intelligence fast

  scene = { ...scene, subject: normalizeSubject(scene.subject, null) || 'main subject' };

  const prevented = preventPromptConflicts(scene);
  scene = prevented.scene;
  const autoDirector = buildAutoDirector(scene);
  const professionalPrompt = buildProfessionalPromptFromScene(scene);
  const conflictReport = detectAdvancedConflicts(professionalPrompt);
  // Skip AI semantic conflicts here to avoid extra API calls — handled by analyzePromptWithGrok
  const semanticConflictReport: AdvancedConflict[] = [];
  const mergedConflictReport = dedupeConflicts([...conflictReport, ...detectSemanticConflictsLocal(professionalPrompt)]);
  const conflictScore = buildConflictScore(mergedConflictReport);
  const fixedPrompt = conflictScore.count > 1
    ? autoRepairFromConflicts(professionalPrompt, mergedConflictReport)
    : resolveAllPromptIssues(professionalPrompt);
  const cinematicPrompt = cinematicUpgradePrompt(fixedPrompt);
  const promptScore = scorePromptForUi(cinematicPrompt, mergedConflictReport);
  const physicsReport = analyzePromptPhysics(cinematicPrompt);
  const cinematicGrammar = buildCinematicGrammarReport(cinematicPrompt);
  const sceneGraph = buildVisualSceneGraph(cinematicPrompt);
  const hollywoodEngine = runHollywoodPromptEngine(cinematicPrompt);

  const result: PromptIntelligenceResult = {
    userPrompt: clean,
    understoodScene: scene,
    finalPrompt: professionalPrompt,
    conflictReport: mergedConflictReport,
    semanticConflictReport,
    conflictScore,
    conflictWarnings: prevented.warnings,
    autoDirector,
    fixedPrompt,
    cinematicPrompt,
    promptScore,
    physicsReport,
    cinematicGrammar,
    cinematicQualityLevel: cinematicGrammar.qualityLevel,
    sceneGraph,
    hollywoodEngine,
  };

  intelligenceCache.set(clean, result);
  return result;

};


export const resolveAllWithGrok = (prompt: string, issues: PromptIssue[]): string => {
  const sorted = issues
    .filter((issue) => issue.type !== 'valid' && issue.fix && issue.end > issue.start)
    .sort((a, b) => b.start - a.start);

  if (!sorted.length) {
    return resolveAllPromptIssues(prompt);
  }

  let next = prompt;
  sorted.forEach((issue) => {
    const start = Math.max(0, Math.min(next.length, issue.start));
    const end = Math.max(start, Math.min(next.length, issue.end));
    if (end <= start) return;

    const replacement = issue.fix?.replacement ?? '';
    next = `${next.slice(0, start)}${replacement}${next.slice(end)}`;
  });

  return resolveAllPromptIssues(next);
};

export const resolvePromptConflictsWithGrok = async (prompt: string, issues: PromptIssue[]): Promise<string> => {
  try {
    const aiResult = await callPromptAI('fix', prompt);
    if (aiResult?.fixed_prompt) return aiResult.fixed_prompt;
    return resolveAllWithGrok(prompt, issues);
  } catch {
    return resolveAllWithGrok(prompt, issues);
  }
};

export const resolvePhysicsWithGrok = async (prompt: string): Promise<string> => {
  const physics = analyzePromptPhysics(prompt);
  if (!physics.length) return prompt;

  try {
    const aiResult = await callPromptAI('fix', prompt);
    if (aiResult?.fixed_prompt) return aiResult.fixed_prompt;
    return fixPromptPhysics(prompt, physics);
  } catch {
    return fixPromptPhysics(prompt, physics);
  }
};

export const enhanceCinematicGrammarWithGrok = async (prompt: string): Promise<string> => {
  try {
    const aiResult = await callPromptAI('enhance', prompt);
    if (aiResult?.enhanced_prompt) return aiResult.enhanced_prompt;
    return enhanceCinematicGrammarLocal(prompt);
  } catch {
    return enhanceCinematicGrammarLocal(prompt);
  }
};

export const enhancePromptWithGrok = async (prompt: string): Promise<string> => {
  try {
    const aiResult = await callPromptAI('enhance', prompt);
    if (aiResult?.enhanced_prompt) return aiResult.enhanced_prompt;
    return resolveAllPromptIssues(prompt);
  } catch {
    return resolveAllPromptIssues(prompt);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// NEW: Direct Gemini API functions for Fix and Enhance buttons
// Uses Gemini instead of Grok (already have API key)
// ─────────────────────────────────────────────────────────────────────────────

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_API_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent';

/**
 * Extract only the prompt from Grok's response
 * Removes any explanatory text or markdown formatting
 */
function extractPromptOnly(response: string): string {
  // Remove markdown code blocks if present
  let clean = response.replace(/```[\w]*\n?/g, '').trim();

  // Remove common prefixes that Grok might add
  const prefixesToRemove = [
    /^(?:here is|here's|this is|the cleaned|the enhanced|the optimized|the final|final prompt:?)/i,
    /^(?:cleaned and enhanced|optimized|enhanced|cleaned) (?:prompt:?|version:?)/i,
    /^(?:prompt:?\s*)/i,
  ];

  for (const prefix of prefixesToRemove) {
    clean = clean.replace(prefix, '').trim();
  }

  // Remove common suffixes/explanations
  const lines = clean.split('\n');
  const promptLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // Skip explanation lines that start with certain patterns
    if (/^(?:note:|explanation:|this prompt|i have|i added|i removed|the changes)/i.test(trimmed)) {
      break;
    }
    if (trimmed) {
      promptLines.push(trimmed);
    }
  }

  return promptLines.join(' ').trim();
}

/**
 * System prompt for Fix button - removes conflicts and duplicates, makes prompt coherent
 */
const FIX_SYSTEM_PROMPT = `You are an expert video and image prompt editor. Your task:

** Required Corrections: **
- Remove any logical conflicts in the prompt (e.g., golden hour with 5500K daylight)
- Remove any repeated words or sentences (duplicates)
- Fix broken or incomplete words
- Connect scene elements logically and coherently
- Keep the same core scene idea without changing it

** Strict Rules: **
- Do not add new details not in the original prompt
- Do not change the core scene concept
- Do not repeat any element
- The prompt must be coherent and logical

** Required Output: **
A single clean, corrected prompt ready to use, without any explanations or comments. Just the final prompt.`;

/**
 * System prompt for Enhance button - makes prompt very professional/cinematic
 */
const ENHANCE_SYSTEM_PROMPT = `You are an expert video and image prompt enhancer. Your task:

** Required Enhancements: **
- Transform the prompt into a highly professional cinematic prompt
- Add cinematic details: precise lighting, camera angle, lens type
- Add mood/atmosphere details
- Add quality details: photorealistic, cinematic lighting, depth of field
- Connect elements more strongly and clearly
- Expand the description so the scene is clearer and more generatable

** Strict Rules: **
- Keep the core scene concept
- Do not repeat any existing element
- Do not add the same details twice
- The prompt must be cinematic and professional

** Required Output: **
A single professional, cinematic prompt ready to use, without any explanations or comments. Just the final prompt.`;

export interface GrokDirectResult {
  ok: boolean;
  prompt: string;
  error?: string;
}

/**
 * Fast local duplicate removal - runs immediately before Grok call
 * Removes exact duplicates and near-duplicates from prompt
 */
export function removeDuplicatesLocal(prompt: string): string {
  if (!prompt.trim()) return prompt;
  
  // Split into sentences/phrases
  const segments = prompt.split(/[.,;]/).map(s => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const unique: string[] = [];
  
  for (const segment of segments) {
    // Normalize for comparison (lowercase, remove extra spaces)
    const normalized = segment.toLowerCase().replace(/\s+/g, ' ').trim();
    
    // Skip if we've seen this exact normalized text
    if (seen.has(normalized)) continue;
    
    // Skip if this is a substring of something we already kept
    const isSubstring = Array.from(seen).some(existing => 
      normalized.includes(existing) || existing.includes(normalized)
    );
    if (isSubstring && normalized.length < 30) continue;
    
    seen.add(normalized);
    unique.push(segment);
  }
  
  // Rejoin with proper punctuation
  return unique.join('. ') + '.';
}

/**
 * Quick local fix - removes duplicates immediately
 * Returns cleaned prompt without waiting for API
 */
export function quickLocalFix(prompt: string): string {
  // إصلاح شامل: إزالة التكرارات، حل التعارضات، تقسيم البرومبت، إصلاح العبارات الضعيفة، تنظيم الوزن، وضمان التوافق مع جميع الأنماط
  let fixed = resolveAllPromptIssues(prompt);

  // تقسيم البرومبت إلى مكونات واضحة
  const structured = require('@/lib/outputStyleFormatter').formatPrompt(fixed, 'structured');

  // إذا كان النمط Weighted أو فيه أوزان، نظم الأوزان
  if (/\(.*:1\.\d\)/.test(fixed) || fixed.includes('weighted')) {
    fixed = require('@/lib/outputStyleFormatter').formatPrompt(fixed, 'weighted');
  }

  // تنظيف البرومبت ليكون نص طبيعي
  fixed = require('@/lib/outputStyleFormatter').formatPrompt(fixed, 'natural');

  // إعادة توليد جميع الأنماط من البرومبت المنظم
  // (Structured, Weighted, Tags, JSON, YAML, DSL, Markdown, Bullet, Graph)
  // البرومبت الأساسي دائمًا يُصلح أولاً ثم تُشتق منه كل الأنماط
  return fixed;
}

/**
 * Fix prompt using Gemini API directly
 * Removes conflicts (red) and duplicates (orange), fixes and makes coherent
 */
export async function fixPromptWithGrokDirect(rawPrompt: string): Promise<GrokDirectResult> {
  const prompt = (rawPrompt ?? '').trim();

  if (!prompt) {
    return { ok: false, prompt: '', error: 'Cannot process an empty prompt.' };
  }

  // Try Gemini first
  if (GEMINI_API_KEY) {
    try {
      const response = await fetch(`${GEMINI_API_ENDPOINT}?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              role: 'user',
              parts: [
                { text: `${FIX_SYSTEM_PROMPT}\n\nClean and fix this prompt:\n\n${prompt}` }
              ]
            }
          ],
          generationConfig: {
            temperature: 0.3,
            maxOutputTokens: 1024,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const fixedPrompt = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
        if (fixedPrompt) {
          const cleanPrompt = extractPromptOnly(fixedPrompt);
          return { ok: true, prompt: cleanPrompt };
        }
      }
      console.log('[Fix] Gemini failed, trying Groq...');
    } catch (err) {
      console.log('[Fix] Gemini error, trying Groq...');
    }
  }

  // Fallback to Groq
  return await fixPromptWithGroq(prompt);
}

/**
 * Enhance prompt using Gemini API directly
 * Makes prompt very professional and cinematic
 */
export async function enhancePromptWithGrokDirect(rawPrompt: string): Promise<GrokDirectResult> {
  const prompt = (rawPrompt ?? '').trim();

  if (!prompt) {
    return { ok: false, prompt: '', error: 'Cannot process an empty prompt.' };
  }

  // Try Gemini first
  if (GEMINI_API_KEY) {
    try {
      const response = await fetch(`${GEMINI_API_ENDPOINT}?key=${GEMINI_API_KEY}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [
            {
              role: 'user',
              parts: [
                { text: `${ENHANCE_SYSTEM_PROMPT}\n\nEnhance and professionalize this prompt:\n\n${prompt}` }
              ]
            }
          ],
          generationConfig: {
            temperature: 0.4,
            maxOutputTokens: 1024,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const enhancedPrompt = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
        if (enhancedPrompt) {
          const cleanPrompt = extractPromptOnly(enhancedPrompt);
          return { ok: true, prompt: cleanPrompt };
        }
      }
      console.log('[Enhance] Gemini failed, trying Groq...');
    } catch (err) {
      console.log('[Enhance] Gemini error, trying Groq...');
    }
  }

  // Fallback to Groq
  return await enhancePromptWithGroq(prompt);
}

// ─────────────────────────────────────────────────────────────────────────────
// GROQ API FALLBACK - Used when Gemini fails
// ─────────────────────────────────────────────────────────────────────────────

const GROQ_API_KEY = import.meta.env.VITE_GROQ_API_KEY;
const GROQ_API_ENDPOINT = 'https://api.groq.com/openai/v1/chat/completions';

/**
 * Fix prompt using Groq API (fallback when Gemini fails)
 */
async function fixPromptWithGroq(rawPrompt: string): Promise<GrokDirectResult> {
  const prompt = (rawPrompt ?? '').trim();
  if (!prompt) return { ok: false, prompt: '', error: 'Empty prompt.' };
  if (!GROQ_API_KEY) return { ok: false, prompt, error: 'Groq API key not configured.' };

  try {
    const response = await fetch(GROQ_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'llama-3.1-8b-instant',
        messages: [
          { role: 'system', content: FIX_SYSTEM_PROMPT },
          { role: 'user', content: `Clean and fix this prompt:\n\n${prompt}` },
        ],
        temperature: 0.3,
        max_tokens: 1024,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return { ok: false, prompt, error: `Groq API error (${response.status}): ${errorText}` };
    }

    const data = await response.json();
    const fixedPrompt = data.choices?.[0]?.message?.content?.trim();
    if (!fixedPrompt) return { ok: false, prompt, error: 'Groq returned empty.' };

    return { ok: true, prompt: extractPromptOnly(fixedPrompt) };
  } catch (err) {
    return { ok: false, prompt, error: err instanceof Error ? err.message : 'Groq error' };
  }
}

/**
 * Enhance prompt using Groq API (fallback when Gemini fails)
 */
async function enhancePromptWithGroq(rawPrompt: string): Promise<GrokDirectResult> {
  const prompt = (rawPrompt ?? '').trim();
  if (!prompt) return { ok: false, prompt: '', error: 'Empty prompt.' };
  if (!GROQ_API_KEY) return { ok: false, prompt, error: 'Groq API key not configured.' };

  try {
    const response = await fetch(GROQ_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'llama-3.1-8b-instant',
        messages: [
          { role: 'system', content: ENHANCE_SYSTEM_PROMPT },
          { role: 'user', content: `Enhance and professionalize this prompt:\n\n${prompt}` },
        ],
        temperature: 0.4,
        max_tokens: 1024,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      return { ok: false, prompt, error: `Groq API error (${response.status}): ${errorText}` };
    }

    const data = await response.json();
    const enhancedPrompt = data.choices?.[0]?.message?.content?.trim();
    if (!enhancedPrompt) return { ok: false, prompt, error: 'Groq returned empty.' };

    return { ok: true, prompt: extractPromptOnly(enhancedPrompt) };
  } catch (err) {
    return { ok: false, prompt, error: err instanceof Error ? err.message : 'Groq error' };
  }
}

// ─── BROWSER CONSOLE TEST FUNCTIONS ───
// Run these in browser console to test:
// window.testFixAPI()
// window.testEnhanceAPI()

if (typeof window !== 'undefined') {
  (window as any).testFixAPI = async () => {
    const testPrompt = 'A beautiful beautiful cat sitting sitting in golden hour sunset with 50mm lens 50mm lens';
    console.log('🧪 Testing FIX API with:', testPrompt);
    const result = await fixPromptWithGrokDirect(testPrompt);
    console.log('📊 Result:', result);
    return result;
  };
  
  (window as any).testEnhanceAPI = async () => {
    const testPrompt = 'A cat sitting';
    console.log('🧪 Testing ENHANCE API with:', testPrompt);
    const result = await enhancePromptWithGrokDirect(testPrompt);
    console.log('📊 Result:', result);
    return result;
  };
  
  (window as any).testBothAPIs = async () => {
    console.log('═══════════════════════════════════════');
    console.log('🔍 Testing BOTH APIs...');
    console.log('═══════════════════════════════════════');
    console.log('Gemini Key exists:', !!GEMINI_API_KEY);
    console.log('Groq Key exists:', !!GROQ_API_KEY);
    await (window as any).testFixAPI();
    await (window as any).testEnhanceAPI();
    console.log('═══════════════════════════════════════');
    console.log('✅ Tests complete - check results above');
    console.log('═══════════════════════════════════════');
  };
}
